import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for managing pagination state and API calls
 * @param {Function} fetchFunction - Function to fetch data with pagination params
 * @param {Object} initialParams - Initial parameters for the fetch function
 * @param {number} initialPageSize - Initial page size
 * @returns {Object} Pagination state and handlers
 */
const usePagination = (fetchFunction, initialParams = {}, initialPageSize = 50) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: initialPageSize,
        count: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
    });
    const [params, setParams] = useState(initialParams);
    
    // Use ref to store the fetch function to avoid dependency issues
    const fetchFunctionRef = useRef(fetchFunction);
    fetchFunctionRef.current = fetchFunction;

    // Fetch data with pagination
    const fetchData = useCallback(async (page = 1, pageSize = initialPageSize, searchParams = {}) => {
        setLoading(true);
        setError(null);
        setErrorCode(null);

        try {
            const response = await fetchFunctionRef.current({
                page,
                page_size: pageSize,
                ...searchParams
            });

            // Handle different response formats
            let responseData, responseCount, responseTotalPages, responseHasNext, responseHasPrevious;

            if (response.results !== undefined) {
                // Standard paginated response
                responseData = response.results;
                responseCount = response.count || 0;
                responseTotalPages = response.total_pages || Math.ceil(responseCount / pageSize);
                responseHasNext = response.has_next || false;
                responseHasPrevious = response.has_previous || false;
            } else if (Array.isArray(response)) {
                // Non-paginated response (fallback)
                responseData = response;
                responseCount = response.length;
                responseTotalPages = 1;
                responseHasNext = false;
                responseHasPrevious = false;
            } else {
                // Custom response format
                responseData = response.data || response.items || [];
                responseCount = response.count || response.total || responseData.length;
                responseTotalPages = response.total_pages || Math.ceil(responseCount / pageSize);
                responseHasNext = response.has_next || response.next !== null;
                responseHasPrevious = response.has_previous || response.previous !== null;
            }

            setData(Array.isArray(responseData) ? responseData : []);
            setPagination({
                page,
                pageSize,
                count: responseCount,
                totalPages: responseTotalPages,
                hasNext: responseHasNext,
                hasPrevious: responseHasPrevious,
            });

            return response;
        } catch (err) {
            setError(err.message || 'Failed to fetch data');
            setErrorCode(err.response?.status || null);
            console.error('Pagination fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [initialPageSize]);

    // Initial data fetch - only run once on mount
    useEffect(() => {
        fetchData(1, initialPageSize, initialParams);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle page change
    const handlePageChange = useCallback((newPage) => {
        fetchData(newPage, pagination.pageSize, params);
    }, [fetchData, pagination.pageSize, params]);

    // Handle page size change
    const handlePageSizeChange = useCallback((newPageSize) => {
        fetchData(1, newPageSize, params); // Reset to first page when changing page size
    }, [fetchData, params]);

    // Handle search/filter parameters change
    const handleParamsChange = useCallback((newParams) => {
        setParams(newParams);
        fetchData(1, pagination.pageSize, newParams); // Reset to first page when changing params
    }, [fetchData, pagination.pageSize]);

    // Refresh current page
    const refresh = useCallback(() => {
        fetchData(pagination.page, pagination.pageSize, params);
    }, [fetchData, pagination.page, pagination.pageSize, params]);

    // Go to first page
    const goToFirst = useCallback(() => {
        if (pagination.page !== 1) {
            handlePageChange(1);
        }
    }, [handlePageChange, pagination.page]);

    // Go to last page
    const goToLast = useCallback(() => {
        if (pagination.page !== pagination.totalPages) {
            handlePageChange(pagination.totalPages);
        }
    }, [handlePageChange, pagination.page, pagination.totalPages]);

    // Go to next page
    const goToNext = useCallback(() => {
        if (pagination.hasNext) {
            handlePageChange(pagination.page + 1);
        }
    }, [handlePageChange, pagination.hasNext, pagination.page]);

    // Go to previous page
    const goToPrevious = useCallback(() => {
        if (pagination.hasPrevious) {
            handlePageChange(pagination.page - 1);
        }
    }, [handlePageChange, pagination.hasPrevious, pagination.page]);

    // Get pagination info for display
    const getPaginationInfo = useCallback(() => {
        const { page, pageSize, count, totalPages } = pagination;
        const start = count > 0 ? (page - 1) * pageSize + 1 : 0;
        const end = Math.min(page * pageSize, count);
        
        return {
            start,
            end,
            count,
            totalPages,
            currentPage: page,
            pageSize,
            hasNext: pagination.hasNext,
            hasPrevious: pagination.hasPrevious,
            displayText: count > 0 ? `${start}-${end} of ${count} items` : 'No items',
        };
    }, [pagination]);

    return {
        // Data and state
        data,
        loading,
        error,
        errorCode,
        pagination,
        params,

        // Handlers
        handlePageChange,
        handlePageSizeChange,
        handleParamsChange,
        refresh,
        goToFirst,
        goToLast,
        goToNext,
        goToPrevious,

        // Utilities
        getPaginationInfo,
        fetchData,

        // Convenience properties
        page: pagination.page,
        pageSize: pagination.pageSize,
        count: pagination.count,
        totalPages: pagination.totalPages,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
    };
};

export default usePagination; 